import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Brain, Heart, Scan } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function BodyAwarenessGuide() {
  const navigate = useNavigate();

  const bodyAreas = [
    { area: "Sleep Quality", questions: ["How quickly am I falling asleep?", "Am I waking during the night?", "How rested do I feel in the morning?"] },
    { area: "Energy Levels", questions: ["How is my energy throughout the day?", "Do I crash in the afternoon?", "Can I exercise without feeling drained?"] },
    { area: "Digestion", questions: ["How does my stomach feel?", "Any bloating or discomfort?", "How is my appetite?"] },
    { area: "Skin & Appearance", questions: ["How does my skin look?", "Any puffiness or dark circles?", "How bright are my eyes?"] },
    { area: "Mental Clarity", questions: ["How clear is my thinking?", "Can I focus easily?", "How is my memory?"] },
    { area: "Mood & Emotions", questions: ["How stable are my emotions?", "How is my baseline mood?", "Do I feel anxious or calm?"] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Scan className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Body Awareness Check</h1>
          <p className="text-lg text-secondary-600">Notice how your body responds without alcohol</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Body Awareness Matters</h2>
          <p className="text-secondary-700 mb-4">
            After several alcohol-free days, your body is recalibrating. Tuning into these changes builds motivation to continue and helps you understand alcohol's true effects on your body—effects that were masked when you were drinking regularly.
          </p>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-emerald-800 font-medium">
              🔬 This isn't about judgment—it's about curiosity. What is your body telling you?
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-500" />
            Body Scan Areas
          </h2>
          <div className="space-y-4">
            {bodyAreas.map((item, i) => (
              <div key={i} className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <h3 className="font-bold text-emerald-800 mb-2">{item.area}</h3>
                <ul className="space-y-1">
                  {item.questions.map((q, j) => (
                    <li key={j} className="text-secondary-600 text-sm flex items-center gap-2">
                      <span className="text-emerald-400">•</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-emerald-500" />
            Today's Body Scan
          </h2>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <div>
                <p className="font-medium text-secondary-900">Find a quiet moment</p>
                <p className="text-secondary-600 text-sm">Sit or lie comfortably. Take 3 deep breaths.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <div>
                <p className="font-medium text-secondary-900">Scan from head to toe</p>
                <p className="text-secondary-600 text-sm">Notice your head, face, neck, shoulders, chest, belly, hips, legs, feet.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <div>
                <p className="font-medium text-secondary-900">Rate each area above</p>
                <p className="text-secondary-600 text-sm">Use a 1-10 scale. How does each feel compared to a week ago?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <div>
                <p className="font-medium text-secondary-900">Note improvements</p>
                <p className="text-secondary-600 text-sm">Write down any positive changes, no matter how small.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" />
            Common Improvements People Notice
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {["Better sleep quality", "More morning energy", "Clearer skin", "Less bloating", "Better mood stability", "Improved focus", "Reduced anxiety", "More vivid dreams"].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-rose-50 rounded-lg">
                <span className="text-rose-500">✓</span>
                <span className="text-secondary-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What changes have I noticed in my body this week? What surprised me? How do I feel about continuing to reduce or eliminate alcohol?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default BodyAwarenessGuide;
